/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */

import {
	Component,
	EventEmitter,
	Input,
	Output
} from '@angular/core';
import {
	DrawerAnimation,
	ListDrawerAnimation
} from '@shared/app-animations';
import {
	IDrawerMenuItem
} from '@shared/interfaces/application-objects/drawer-menu-item.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';

/* eslint-enable max-len */

@Component({
	selector: 'app-drawers',
	templateUrl: './app-drawers.component.html',
	styleUrls: ['./app-drawers.component.scss'],
	animations: [
		DrawerAnimation,
		ListDrawerAnimation
	]
})

/**
 * A component representing an instance of a drawer displayed in the
 * drawers component.
 *
 * @export
 * @class AppDrawersComponent
 */
export class AppDrawersComponent
{
	/**
	 * Gets or sets the current set of pinned drawer items.
	 *
	 * @type {IDrawerMenuItem}
	 * @memberof AppDrawersComponent
	 */
	@Input() public pinnedDrawerItems: IDrawerMenuItem[] = [];

	/**
	 * Gets or sets the current overlayed drawer item.
	 *
	 * @type {IDrawerMenuItem}
	 * @memberof AppDrawersComponent
	 */
	@Input() public overlayDrawerItem: IDrawerMenuItem;

	/**
	 * Gets or sets the page context used for linking drawer displays to
	 * page content.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof AppDrawersComponent
	 */
	@Input() public pageContext: IDynamicComponentContext<Component, any>;

	/**
	 * Gets or sets the event that will be emitted to all listening components
	 * when a pin drawer button is clicked.
	 *
	 * @type {EventEmitter<IDrawerMenuItem>}
	 * @memberof AppDrawersComponent
	 */
	@Output() public drawerPinButtonClicked: EventEmitter<IDrawerMenuItem> =
		new EventEmitter();

	/**
	 * Gets or sets the event that will be emitted to all listening components
	 * when a close drawer button is clicked.
	 *
	 * @type {EventEmitter<IDrawerMenuItem>}
	 * @memberof AppDrawersComponent
	 */
	@Output() public drawerCloseButtonClicked: EventEmitter<IDrawerMenuItem> =
		new EventEmitter();

	/**
	 * Handles the close drawer click by bubbling this event up to the
	 * containing command component.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The menu item that received the close click event.
	 * @memberof AppDrawersComponent
	 */
	public closeDrawerClick(
		drawerMenuItem: IDrawerMenuItem): void
	{
		this.drawerCloseButtonClicked.emit(
			drawerMenuItem);
	}

	/**
	 * Handles the pin drawer click by bubbling this event up to the containing
	 * command component.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The menu item that received the pin click event.
	 * @memberof AppDrawersComponent
	 */
	public pinDrawerClick(
		drawerMenuItem: IDrawerMenuItem): void
	{
		this.drawerPinButtonClicked.emit(
			drawerMenuItem);
	}
}